import {
  ChartBarIcon,
  UserGroupIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowUpIcon,
  DocumentTextIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline'

export default function TrainingStatsHelp() {
  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="flex items-center mb-6">
          <ChartBarIcon className="h-8 w-8 text-green-600 mr-3" />
          <h1 className="text-3xl font-bold text-gray-900">Training Statistics</h1>
        </div>

        <div className="prose prose-lg max-w-none">
          <p className="text-gray-600 mb-8">
            Training Statistics provides comprehensive analytics and insights into your organization's training programs.
            Track completion rates, assess performance, monitor engagement, and identify areas for improvement.
          </p>

          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Key Features</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <ArrowUpIcon className="h-6 w-6 text-blue-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Performance Analytics</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Detailed performance metrics including completion rates, scores, and learning progress tracking.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <UserGroupIcon className="h-6 w-6 text-green-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Learner Insights</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Individual and group performance analysis to identify training needs and skill gaps.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <DocumentTextIcon className="h-6 w-6 text-purple-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Course Analytics</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Comprehensive data on course effectiveness, engagement levels, and completion trends.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <AcademicCapIcon className="h-6 w-6 text-orange-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Certification Tracking</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Monitor certification status, renewal dates, and compliance requirements.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Dashboard Overview</h2>

          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Performance Indicators</h3>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
                <div className="text-2xl font-bold text-blue-600">87%</div>
                <div className="text-sm text-gray-600">Completion Rate</div>
              </div>
              <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
                <div className="text-2xl font-bold text-green-600">4.2</div>
                <div className="text-sm text-gray-600">Avg Score</div>
              </div>
              <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
                <div className="text-2xl font-bold text-purple-600">156</div>
                <div className="text-sm text-gray-600">Active Learners</div>
              </div>
              <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
                <div className="text-2xl font-bold text-orange-600">23</div>
                <div className="text-sm text-gray-600">Courses</div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <h4 className="font-medium text-gray-900 mb-3">Training Course Performance</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div>
                    <h5 className="font-medium text-green-900">Safety Training</h5>
                    <p className="text-sm text-green-700">95% completion • 4.8 avg score</p>
                  </div>
                  <CheckCircleIcon className="h-6 w-6 text-green-600" />
                </div>

                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div>
                    <h5 className="font-medium text-blue-900">Compliance Procedures</h5>
                    <p className="text-sm text-blue-700">78% completion • 3.9 avg score</p>
                  </div>
                  <ClockIcon className="h-6 w-6 text-blue-600" />
                </div>

                <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                  <div>
                    <h5 className="font-medium text-yellow-900">Advanced Customer Service</h5>
                    <p className="text-sm text-yellow-700">65% completion • 4.1 avg score</p>
                  </div>
                  <ClockIcon className="h-6 w-6 text-yellow-600" />
                </div>

                <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <div>
                    <h5 className="font-medium text-red-900">Technical Systems</h5>
                    <p className="text-sm text-red-700">42% completion • 3.2 avg score</p>
                  </div>
                  <XCircleIcon className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Individual Learner Tracking</h2>

          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Learner Progress Dashboard</h3>

            <div className="space-y-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-blue-600 font-semibold">JD</span>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">John Doe</h4>
                      <p className="text-sm text-gray-600">Sales Representative</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold text-green-600">8/10</div>
                    <div className="text-sm text-gray-600">Courses Completed</div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="text-center">
                    <div className="font-semibold text-gray-900">4.5</div>
                    <div className="text-gray-600">Avg Score</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-blue-600">12h 30m</div>
                    <div className="text-gray-600">Time Spent</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-green-600">85%</div>
                    <div className="text-gray-600">Success Rate</div>
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-green-600 font-semibold">SM</span>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">Sarah Miller</h4>
                      <p className="text-sm text-gray-600">Customer Service Lead</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold text-blue-600">6/10</div>
                    <div className="text-sm text-gray-600">Courses Completed</div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="text-center">
                    <div className="font-semibold text-gray-900">4.1</div>
                    <div className="text-gray-600">Avg Score</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-blue-600">8h 45m</div>
                    <div className="text-gray-600">Time Spent</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-yellow-600">72%</div>
                    <div className="text-gray-600">Success Rate</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Quiz Performance Analysis</h2>

          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Assessment Results</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <h4 className="font-medium text-gray-900 mb-3">Score Distribution</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>90-100%</span>
                    <span className="font-semibold text-green-600">23 learners</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-green-500 h-2 rounded-full" style={{width: '35%'}}></div>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span>80-89%</span>
                    <span className="font-semibold text-blue-600">31 learners</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-500 h-2 rounded-full" style={{width: '47%'}}></div>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span>70-79%</span>
                    <span className="font-semibold text-yellow-600">18 learners</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-yellow-500 h-2 rounded-full" style={{width: '27%'}}></div>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span>Below 70%</span>
                    <span className="font-semibold text-red-600">8 learners</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-red-500 h-2 rounded-full" style={{width: '12%'}}></div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <h4 className="font-medium text-gray-900 mb-3">Difficulty Analysis</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Easy Quizzes</span>
                    <div className="flex items-center">
                      <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{width: '85%'}}></div>
                      </div>
                      <span className="text-sm font-semibold text-green-600">85%</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm">Medium Quizzes</span>
                    <div className="flex items-center">
                      <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                        <div className="bg-blue-500 h-2 rounded-full" style={{width: '72%'}}></div>
                      </div>
                      <span className="text-sm font-semibold text-blue-600">72%</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm">Hard Quizzes</span>
                    <div className="flex items-center">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                          <div className="bg-red-500 h-2 rounded-full" style={{width: '45%'}}></div>
                        </div>
                        <span className="text-sm font-semibold text-red-600">45%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-gray-900 mb-4">How to Use</h2>

          <div className="space-y-6 mb-8">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-green-600 font-semibold text-sm">1</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Access Dashboard</h4>
                <p className="text-gray-600 text-sm">Navigate to Training Stats to view overall performance metrics and KPIs.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-green-600 font-semibold text-sm">2</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Filter by Department</h4>
                <p className="text-gray-600 text-sm">Use department filters to analyze performance by team or business unit.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-green-600 font-semibold text-sm">3</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Track Individual Progress</h4>
                <p className="text-gray-600 text-sm">Monitor individual learner progress and identify those needing additional support.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-green-600 font-semibold text-sm">4</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Analyze Course Effectiveness</h4>
                <p className="text-gray-600 text-sm">Review course completion rates and assessment scores to improve content quality.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-green-600 font-semibold text-sm">5</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Generate Reports</h4>
                <p className="text-gray-600 text-sm">Export detailed reports for compliance, management review, or training planning.</p>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Best Practices</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-start">
                <ArrowUpIcon className="h-6 w-6 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-blue-900 mb-2">Performance Monitoring</h4>
                  <ul className="text-blue-800 text-sm space-y-1">
                    <li>• Set clear completion targets for each course</li>
                    <li>• Monitor assessment scores regularly</li>
                    <li>• Identify and address knowledge gaps promptly</li>
                    <li>• Track improvement trends over time</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-start">
                <UserGroupIcon className="h-6 w-6 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-green-900 mb-2">Learner Support</h4>
                  <ul className="text-green-800 text-sm space-y-1">
                    <li>• Provide additional resources for struggling learners</li>
                    <li>• Recognize and reward high performers</li>
                    <li>• Offer remedial training when needed</li>
                    <li>• Gather feedback to improve course content</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <div className="flex items-start">
              <CheckCircleIcon className="h-6 w-6 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-yellow-900 mb-2">Compliance & Certification</h4>
                <ul className="text-yellow-800 text-sm space-y-1 mb-3">
                  <li>• Track mandatory training completion</li>
                  <li>• Monitor certification expiry dates</li>
                  <li>• Generate compliance reports for audits</li>
                  <li>• Set up automated renewal reminders</li>
                </ul>
                <div className="bg-white rounded-lg p-3 border border-yellow-300">
                  <p className="text-yellow-800 text-sm">
                    <strong>Important:</strong> Use training statistics to ensure regulatory compliance
                    and maintain accurate certification records for your organization.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}